import { CustomFieldValue } from "../../../contract/contract/Contract.types.ts";
import {
    ContractStandardField,
    DateFieldReference,
    DatePeriodConfiguration,
    FieldConfigurationData,
    FieldConfigurationValueType,
    PeriodUnit,
} from "../../EntityTemplate.types.ts";

export type DateValue = Date | string | null | undefined;

// The values a computed field reads from the entity it is shown on. Only contracts have standard dates.
export interface ComputedFieldEntityValues {
    customFieldValues: Array<CustomFieldValue>;
    startDate?: DateValue;
    endDate?: DateValue;
}

const monthsPerYear = 12;
const millisecondsPerDay = 24 * 60 * 60 * 1000;
const standardDateOptionPrefix = "standard:";
const customDateOptionPrefix = "custom:";

// Calendar months stepped from the start date plus the leftover days as a fraction of the month they fall in.
// The result is left unrounded so formulas give the same total whichever unit is chosen.
// Mirrors DatePeriodCalculator in the backend.
export function calculatePeriod(start: DateValue, end: DateValue, unit: PeriodUnit): number | undefined {
    const periodStart = toCalendarDay(start);
    const periodEnd = toCalendarDay(end);
    if (periodStart === undefined || periodEnd === undefined) {
        return undefined;
    }

    const periodEndExclusive = getPeriodEndExclusive(periodStart, periodEnd);
    if (periodEndExclusive <= periodStart) {
        return undefined;
    }

    const months = countMonths(periodStart, periodEndExclusive);
    return unit === PeriodUnit.Years ? months / monthsPerYear : months;
}

export function isEndBeforeStart(start: DateValue, end: DateValue): boolean {
    const periodStart = toCalendarDay(start);
    const periodEnd = toCalendarDay(end);
    return periodStart !== undefined && periodEnd !== undefined && periodEnd < periodStart;
}

export function parseDatePeriod(configurationData: FieldConfigurationData | null | undefined): DatePeriodConfiguration | undefined {
    if (configurationData?.type !== FieldConfigurationValueType.Json) {
        return undefined;
    }

    return JSON.parse(configurationData.value) as DatePeriodConfiguration;
}

export function serializeDatePeriod(configuration: DatePeriodConfiguration): FieldConfigurationData {
    return { type: FieldConfigurationValueType.Json, value: JSON.stringify(configuration) };
}

export function calculateConfiguredPeriod(configuration: DatePeriodConfiguration | undefined, entity: ComputedFieldEntityValues): number | undefined {
    if (!configuration) {
        return undefined;
    }

    return calculatePeriod(resolveDateValue(configuration.from, entity), resolveDateValue(configuration.to, entity), configuration.unit);
}

export function resolveDateValue(reference: DateFieldReference, entity: ComputedFieldEntityValues): DateValue {
    if (reference.standardField === ContractStandardField.StartDate) {
        return entity.startDate;
    }

    if (reference.standardField === ContractStandardField.EndDate) {
        return entity.endDate;
    }

    return findCustomFieldValue(reference.customFieldId, entity);
}

export function findCustomFieldValue(fieldId: string | undefined, entity: ComputedFieldEntityValues): any {
    return entity.customFieldValues.find((customFieldValue) => customFieldValue.customInputFieldId === fieldId)?.value;
}

export function getPeriodUnitTranslationKey(unit: PeriodUnit | undefined): string {
    return unit === PeriodUnit.Months ? "monthsShort" : "yearsShort";
}

// The from and to selects share one list of options holding both standard and custom dates, so each option id says which kind it is
export function toDateOptionId(reference: DateFieldReference | undefined): string | null {
    if (reference?.standardField) {
        return `${standardDateOptionPrefix}${reference.standardField}`;
    }

    return reference?.customFieldId ? `${customDateOptionPrefix}${reference.customFieldId}` : null;
}

export function fromDateOptionId(optionId: string | null): DateFieldReference {
    if (optionId?.startsWith(standardDateOptionPrefix)) {
        return { standardField: Number(optionId.substring(standardDateOptionPrefix.length)) };
    }

    return optionId ? { customFieldId: optionId.substring(customDateOptionPrefix.length) } : {};
}

// Contracts are written both as 1 Jan to 31 Dec and as 1 Jul to 1 Jul, and both mean whole years. So the end date counts
// as part of the period, except when it falls on the start date's day of the month, where it is read as the anniversary
function getPeriodEndExclusive(periodStart: Date, periodEnd: Date): Date {
    return periodEnd > periodStart && periodEnd.getUTCDate() === periodStart.getUTCDate() ? periodEnd : addDays(periodEnd, 1);
}

function countMonths(periodStart: Date, periodEndExclusive: Date): number {
    const wholeMonths = countWholeMonths(periodStart, periodEndExclusive);
    const anchor = addMonthsClamped(periodStart, wholeMonths);
    const remainderDays = Math.round((periodEndExclusive.getTime() - anchor.getTime()) / millisecondsPerDay);
    return wholeMonths + remainderDays / daysInMonth(anchor.getUTCFullYear(), anchor.getUTCMonth());
}

function countWholeMonths(periodStart: Date, periodEndExclusive: Date): number {
    const calendarMonthsBetween =
        (periodEndExclusive.getUTCFullYear() - periodStart.getUTCFullYear()) * monthsPerYear + periodEndExclusive.getUTCMonth() - periodStart.getUTCMonth();
    return periodEndExclusive.getUTCDate() < periodStart.getUTCDate() ? calendarMonthsBetween - 1 : calendarMonthsBetween;
}

// setMonth rolls 31 January + 1 month over into March, whereas the backend clamps to the end of February like SQL DATEADD
function addMonthsClamped(date: Date, months: number): Date {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + months;
    return new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), daysInMonth(year, month))));
}

function daysInMonth(year: number, month: number): number {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * millisecondsPerDay);
}

// Dates are compared as calendar days at UTC midnight, so neither the time of day nor daylight saving time shifts a period
function toCalendarDay(value: DateValue): Date | undefined {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    // A bare date string would otherwise be read as UTC and land on the day before in time zones west of UTC
    const date = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    if (isNaN(date.getTime())) {
        return undefined;
    }

    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}
